var Application = require("enyo/Application"),
    Router      = require("app/core/Router"),
    kind        = require("enyo/kind"),
    logger      = require("enyo/logger"),
    master      = require("enyo/master");

module.exports = kind(/** @lends  app.core.App.prototype */ {

    /**
     * @private
     */
    name: "app.core.App",

    /**
     * @private
     */
    kind: Application,

    /**
     * Components that will be generating parts of the UI. These will be
     * initialized after the view.
     *
     * @private
     */
    UiComponents: null,

    /**
     * We don't use enyo's autostart. We need the freedom to do other things
     * first, such as generating UiComponents after the application view has
     * been initialized.
     *
     * @private
     */
    autoStart: false,

    /**
     * @method
     * @private
     */
    constructor: kind.inherit(function(sup) {
        return function() {
            this.pages        = [];
            this.mediaDetails = [];
            sup.apply(this, arguments);
        };
    }),

    /**
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            // Enyo's Application will create the components defined in
            // `components` first. Since we do not have a view yet at that time,
            // none of the components should be adding anything to the UI yet.
            //
            // Once all components are initialized, enyo's Application will
            // initialize the view.
            sup.apply(this, arguments);

            // Now that the view is initialized, it is safe to initialized
            // components that will be adding things to the UI.
            //
            // The main benefit of only initializing these after the view is
            // that the view will receive DOM events (such as onresize) first,
            // and any UI that will have been injected into the layout will
            // receive them later.
            this.createClientComponents(this.UiComponents);
        };
    }),

    /**
     * @method
     * @private
     */
    initComponents: kind.inherit(function(sup) {
        return function() {
            this.createRouter();
            sup.apply(this, arguments);
        };
    }),

    /**
     * @private
     */
    createRouter: function() {
        this.createComponent({
            name: "router",
            kind: Router
        });
    },

    /**
     * @method
     * @private
     */
    ownerChanged: kind.inherit(function(sup) {
        return function() {
            var floatingLayer;

            // If we're attaching to master, make sure we're added *before* the
            // floatingLayer. This ensures resize events are first triggered on
            // the app view instead of on the floatingLayer. This way, once
            // controls in the floatingLayer do reflow they can be sure the app
            // layout is already up-to-date and we can safely align floating
            // items relative to the main app layout.
            if (this.owner === master) {
                floatingLayer    = master.$.control;
                delete master.$.control;
                sup.apply(this, arguments);
                master.$.control = floatingLayer;
            }
            else {
                sup.apply(this, arguments);
            }
        };
    }),

    /**
    * @method
    * @private
    */
    render: kind.inherit(function(sup) {
        return function() {
            var children;

            // Remove noscript tags
            Array.from(document.querySelectorAll("noscript")).forEach(function(node) {
                node.remove();
            });

            // Add styles to icon sprite
            document.querySelector("svg").classList.add("app__icon-sprite");

            // Cache body tag child nodes
            children = Array.from(document.body.children);

            // Render app view into body
            sup.apply(this, arguments);

            // Re-attach children
            children.forEach(function(child) {
                if (child.classList.contains("app__icon-sprite")) {
                    document.body.prepend(child);
                }
                else {
                    document.body.append(child);
                }
            });
        };
    }),

    // Pages
    // --------------------------------

    /**
     * All instantiated pages.
     *
     * @type {Array}
     * @private
     */
    pages: null,

    /**
     * The activation promise
     *
     * @type  {Promise}
     * @private
     */
    pagePromise: null,

    /**
     * Activates a particular page.
     *
     * @param  {Function}  Controller            The controller kind
     * @param  {Object}    params                The parameters to be passed to
     *                                           the controller.
     * @param  {boolean}   isDocumentTitleOwner  Whether the page should control
     *                                           the document title
     * @public
     */
    activatePage: function(Controller, params, isDocumentTitleOwner) {
        var newPage,
            pagePromise;

        // Search for an existing instance of the page.
        newPage = this.pages.find(function(page) {
            return page instanceof Controller;
        });

        // No page found, create a new instance
        if (!newPage) {
            newPage = this.createComponent({
                kind:          Controller,
                viewContainer: this.view.$.contentRegion
            });
            this.pages.push(newPage);
        }

        // Mark as documentTitleOwner as needed
        isDocumentTitleOwner && this.set("documentTitleOwner", newPage);

        // Activate the page
        pagePromise = newPage.activate(params);

        if (pagePromise !== this.pagePromise) {
            this.pagePromise = pagePromise;
            pagePromise
                .then(this.bindSafely(function() {
                    var errorMsg;

                    switch (newPage.get("active")) {

                        // Page was not activated. Discard it.
                        case -1:
                            this.discardPage(newPage);
                            break;

                        // Page was successfully activated. We can prune all
                        // other pages.
                        case 1:
                            this.pruneOtherPages(newPage);
                            break;

                        // Page is already pending with the next action
                        case 0:
                            break;
                        default:
                            errorMsg = "Invalid page activation status";
                            logger.error(errorMsg);

                            if (typeof Sentry !== "undefined") {
                                Sentry.captureException(new Error(errorMsg));
                            }
                            break;
                    }
                }))
                .catch(this.bindSafely(function(error) {
                    logger.error(error);

                    if (typeof Sentry !== "undefined" && error instanceof Error) {
                        Sentry.captureException(error);
                    }

                    // Page activation failed. Discard it.
                    this.discardPage(newPage);
                }));
        }
    },

    /**
     * Destroys and removes all pages that aren't the one we're keeping.
     *
     * @param  {app.core.ViewController}  keeper  The page that won't be
     *                                            discarded.
     * @private
     */
    pruneOtherPages: function(keeper) {
        this.pages = this.pages.filter(function(page) {
            if (page !== keeper) {
                page.destroy();
            }

            return page === keeper;
        });
    },

    /**
     * Searches for the rejected page, destroys it and removes it from the pages
     * array. If no page is provided, all pages get destroyed.
     *
     * @param  {?app.core.ViewController}  reject  The page that will be
     *                                             discarded.
     * @private
     */
    discardPage: function(reject) {
        reject = reject || null;

        this.pages = this.pages.filter(function(page) {
            if (page === reject || reject === null) {
                page.destroy();

                return false;
            }

            return false;
        });
    },

    // Media Details
    // --------------------------------

    /**
     * All instantiated media details.
     *
     * @type {Array}
     * @private
     */
    mediaDetails: null,

    /**
     * The activation promise
     *
     * @type  {Promise}
     * @private
     */
    mediaDetailsPromise: null,

    /**
     * Activates a particular media details view.
     *
     * @param  {Function}  Controller            The controller kind
     * @param  {Object}    params                The parameters to be passed to
     *                                           the controller.
     * @param  {boolean}   isDocumentTitleOwner  Whether the popup should
     *                                           control the document title
     * @public
     */
    activateMediaDetails: function(Controller, params, isDocumentTitleOwner) {
        var $mediaDetailsPopup = this.view.$.mediaDetailsPopup,
            newMediaDetails,
            mediaDetailsPromise;

        // Ensure the popup is rendered, so we can put stuff in it.
        if (!$mediaDetailsPopup.hasNode()) {
            $mediaDetailsPopup.set("canGenerate", true);
            $mediaDetailsPopup.render();
        }

        // Search for an existing instance of the media details.
        newMediaDetails = this.mediaDetails.find(function(details) {
            return details instanceof Controller;
        });

        // No media details found, create a new instance
        if (!newMediaDetails) {
            newMediaDetails = this.createComponent({
                kind:          Controller,
                popup:         $mediaDetailsPopup,
                viewContainer: $mediaDetailsPopup.controlParent
            });
            this.mediaDetails.push(newMediaDetails);
        }

        // Mark as documentTitleOwner as needed
        isDocumentTitleOwner && this.set("documentTitleOwner", newMediaDetails);

        // Reveal the popup and activate it's new contents
        $mediaDetailsPopup.set("controller", newMediaDetails);
        $mediaDetailsPopup.requestShow(true, true);
        mediaDetailsPromise = newMediaDetails.activate(params);

        if (mediaDetailsPromise !== this.mediaDetailsPromise) {
            this.mediaDetailsPromise = mediaDetailsPromise;

            mediaDetailsPromise
                .then(this.bindSafely(function() {
                    var errorMsg;

                    switch (newMediaDetails.get("active")) {

                        // MediaDetails were not activated. Discard them.
                        case -1:
                            this.discardMediaDetails(newMediaDetails);
                            break;

                        // MediaDetails were successfully activated. We can prune
                        // all other details.
                        case 1:
                            this.pruneOtherMediaDetails(newMediaDetails);
                            break;

                        // MediaDetails already pending with the next action
                        case 0:
                            break;
                        default:
                            errorMsg = "Invalid MediaDetails activation status";
                            logger.error(errorMsg);

                            if (typeof Sentry !== "undefined") {
                                Sentry.captureException(new Error(errorMsg));
                            }
                            break;
                    }
                }))
                .catch(this.bindSafely(function(error) {
                    logger.error(error);

                    if (typeof Sentry !== "undefined" && error instanceof Error) {
                        Sentry.captureException(error);
                    }

                    // Media details activation failed. Discard them.
                    this.discardMediaDetails(newMediaDetails);
                }));
        }
    },

    /**
     * Destroys and removes all media details that aren't the one we're keeping.
     *
     * @param  {app.core.ViewController}  keeper  The media details that won't
     *                                            be discarded.
     * @private
     */
    pruneOtherMediaDetails: function(keeper) {
        this.mediaDetails = this.mediaDetails.filter(function(details) {
            if (details !== keeper) {
                details.destroy();
            }

            return details === keeper;
        });
    },

    /**
     * Searches for the rejected media details, destroys it and removes it from
     * the mediaDetails array.
     *
     * @param  {app.core.ViewController}  reject  The media details that will be
     *                                            discarded.
     * @private
     */
    discardMediaDetails: function(reject) {
        var $mediaDetailsPopup = this.view.$.mediaDetailsPopup;

        this.mediaDetails = this.mediaDetails.filter(function(details) {
            if (details === reject) {
                details.destroy();
            }

            return details !== reject;
        });

        // If all mediaDetails are now discarded, hide the popup.
        if (this.mediaDetails.length === 0) {
            if ($mediaDetailsPopup.get("controller") === reject) {
                $mediaDetailsPopup.set("controller", null);
            }
            $mediaDetailsPopup.requestHide(true, false);
        }
    },

    /**
     * Deactivates and destroys all mediaDetails.
     *
     * @public
     */
    deactivateMediaDetails: function() {
        var $mediaDetailsPopup = this.view.$.mediaDetailsPopup;

        this.mediaDetails.forEach(function(details) {
            details.destroy();
        });
        this.mediaDetails = [];

        $mediaDetailsPopup.set("controller", null);
        $mediaDetailsPopup.requestHide(true, false);
    },

    // Document Title
    // --------------------------------

    /**
     * @param  {string}  title  The title
     * @public
     */
    setDocumentTitle: function(title) {
        if (title && document.title !== title) {
            document.title = title;
        }
    }

});
